<template>
  <Drawer
    :aria-label="title"
    data-cy="api-key-form"
    @hide-drawer="emit('close')"
  >
    <template #title>
      <div class="pr-10">
        <h2 class="mb-1 text-base font-semibold text-gray-900">{{ title }}</h2>
        <p v-if="!key" class="m-0 text-sm text-gray-600">
          {{
            isRoll
              ? "The new key gets the same access. The current key keeps working until you revoke it, so you can switch over without downtime."
              : "Give a key to software that reads your Sera data, for example to look up the customer named in a webhook event."
          }}
        </p>
      </div>
    </template>

    <div v-if="key" class="space-y-4 px-4 pb-4" data-cy="api-key-shown">
      <Alert type="warning" data-cy="api-key-once">
        <strong>{{ API_KEY_SHOWN_ONCE }}</strong>
      </Alert>
      <div class="flex flex-wrap items-center gap-2">
        <code
          class="min-w-0 flex-1 break-all rounded border border-gray-200 bg-gray-50 p-2 text-xs text-gray-900"
          data-cy="api-key-value"
          >{{ key }}</code
        >
        <button
          type="button"
          :class="SMALL_SECONDARY"
          data-cy="api-key-copy"
          @click="copyKey"
        >
          {{ copied ? "Copied" : "Copy" }}
        </button>
      </div>
      <p class="m-0 text-sm text-gray-600">
        Send it as <code>Authorization: Bearer &lt;key&gt;</code>. Store it
        where you keep passwords. If it's lost or leaked, revoke it and create
        another.
      </p>
    </div>

    <form
      v-else
      id="api-key-form"
      class="space-y-5 px-4 pb-4"
      novalidate
      @submit.prevent="save"
    >
      <Alert
        v-if="baseError"
        type="error"
        data-cy="api-key-form-error"
        :text="baseError"
      />

      <div v-if="isRoll" data-cy="api-key-roll-summary">
        <div class="text-sm font-medium text-gray-900">
          {{ rolling!.apiKey.name }}
        </div>
        <div class="text-xs text-gray-600">
          Replaces <code>{{ rolling!.credential.token_prefix }}…</code>, with
          access to: {{ scopeNames(rolling!.credential.scopes) }}
        </div>
      </div>

      <div v-else>
        <label
          for="api-key-name"
          class="mb-1 block text-sm font-medium text-gray-900"
          >Name</label
        >
        <input
          id="api-key-name"
          v-model="name"
          type="text"
          maxlength="100"
          autocomplete="off"
          placeholder="What uses this key, for example Office scripts"
          class="block w-full rounded-lg border bg-gray-50 p-2.5 text-sm text-gray-900"
          :class="
            fieldError('name')
              ? 'border-red-500 focus:border-red-500'
              : 'border-gray-300 focus:border-blue-500'
          "
          :aria-invalid="!!fieldError('name')"
          data-cy="api-key-name"
        />
        <p
          v-if="fieldError('name')"
          class="m-0 mt-1 text-xs text-red-700"
          data-cy="api-key-name-error"
        >
          {{ fieldError("name") }}
        </p>
      </div>

      <fieldset v-if="!isRoll">
        <legend class="mb-1 text-sm font-medium text-gray-900">Access</legend>
        <p class="m-0 mb-2 text-xs text-gray-500">
          Choose what this key can read. Keys can only read, never change
          anything.
        </p>
        <p
          v-if="fieldError('scopes')"
          class="m-0 mb-2 text-xs text-red-700"
          data-cy="api-key-scopes-error"
        >
          {{ fieldError("scopes") }}
        </p>
        <div class="overflow-hidden rounded-lg border border-gray-200">
          <label
            class="flex cursor-pointer items-start gap-2 border-b border-gray-200 bg-gray-50 px-3 py-2 text-sm"
          >
            <input
              type="checkbox"
              class="mt-1"
              :checked="allSelected"
              :indeterminate="someSelected"
              data-cy="api-key-general-access"
              @change="toggleAll"
            />
            <span>
              <span class="font-medium text-gray-900">General access</span>
              <span class="block text-xs text-gray-600"
                >Everything below. Scopes added later aren't included: a key
                gets only what it was given.</span
              >
            </span>
          </label>
          <label
            v-for="scope in scopes"
            :key="scope.key"
            class="flex cursor-pointer items-start gap-2 border-b border-gray-100 px-3 py-2 text-sm last:border-b-0"
          >
            <input
              v-model="selectedScopes"
              type="checkbox"
              class="mt-1"
              :value="scope.key"
              :data-cy="`api-key-scope:${scope.key}`"
            />
            <span>
              <span class="font-medium text-gray-900">{{ scope.name }}</span>
              <code class="ml-1 text-xs text-gray-500">{{ scope.key }}</code>
              <span class="block text-xs text-gray-600">{{
                scope.description
              }}</span>
            </span>
          </label>
        </div>
      </fieldset>

      <div>
        <label
          for="api-key-expiry"
          class="mb-1 block text-sm font-medium text-gray-900"
          >Expires</label
        >
        <select
          id="api-key-expiry"
          v-model="expiresInDays"
          class="block w-full rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500"
          data-cy="api-key-expiry"
        >
          <option
            v-for="days in expiryChoices"
            :key="days ?? 'never'"
            :value="days"
          >
            {{ expiryChoiceLabel(days) }}
          </option>
        </select>
        <p
          v-if="fieldError('expires_in_days')"
          class="m-0 mt-1 text-xs text-red-700"
        >
          {{ fieldError("expires_in_days") }}
        </p>
        <p v-else class="m-0 mt-1 text-xs text-gray-500">
          An expired key stops working. You can always revoke a key sooner.
        </p>
      </div>
    </form>

    <template #footer>
      <div class="flex w-full justify-end gap-2 px-4 py-3">
        <button
          v-if="key"
          type="button"
          class="rounded-lg bg-blue-700 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800"
          data-cy="api-key-done"
          @click="emit('close')"
        >
          Done
        </button>
        <template v-else>
          <button
            type="button"
            :class="SECONDARY"
            data-cy="api-key-cancel"
            @click="emit('close')"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="api-key-form"
            class="rounded-lg bg-blue-700 px-4 py-2 text-sm font-medium text-white hover:bg-blue-800 disabled:opacity-60"
            :disabled="saving"
            data-cy="api-key-save"
          >
            {{
              saving ? "Creating…" : isRoll ? "Create new key" : "Create key"
            }}
          </button>
        </template>
      </div>
    </template>
  </Drawer>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useStore } from "vuex";

import Alert from "@/components/Seranova/Alert/Alert.vue";
import Drawer from "@/components/Seranova/Drawer/Drawer.vue";
import type {
  ApiKey,
  ApiKeyCredential,
  ApiKeyScope,
  MintedApiKey
} from "@/vuex/modules/apiKeys";
import {
  API_KEY_SHOWN_ONCE,
  apiKeyError,
  expiryChoiceLabel
} from "./apiAccess";

const props = withDefaults(
  defineProps<{
    scopes: ApiKeyScope[];
    expiryDays: number[];
    // Roll when given: a new key for the same app with the same scopes.
    rolling?: { apiKey: ApiKey; credential: ApiKeyCredential } | null;
  }>(),
  { rolling: null }
);
const emit = defineEmits<{ close: []; minted: [apiKey: ApiKey] }>();

const SECONDARY =
  "rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100";
const SMALL_SECONDARY =
  "rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100";

const store = useStore();

const isRoll = computed(() => !!props.rolling);
const name = ref("");
const selectedScopes = ref<string[]>([]);
const expiresInDays = ref<number | null>(null);
const saving = ref(false);
const baseError = ref("");
const fieldErrors = ref<Record<string, string[]>>({});
// The minted key lives only here, for as long as the drawer is open.
const key = ref<string | null>(null);
const copied = ref(false);

const title = computed(() => {
  if (key.value) return "Your new API key";
  return isRoll.value ? "Roll API key" : "Create an API key";
});

// No expiry is the default (and first) choice.
const expiryChoices = computed<(number | null)[]>(() => [
  null,
  ...props.expiryDays
]);

const allSelected = computed(
  () =>
    props.scopes.length > 0 &&
    props.scopes.every((scope) => selectedScopes.value.includes(scope.key))
);
const someSelected = computed(
  () => selectedScopes.value.length > 0 && !allSelected.value
);

function toggleAll() {
  selectedScopes.value = allSelected.value
    ? []
    : props.scopes.map((scope) => scope.key);
}

function scopeNames(keys: string[]): string {
  return keys
    .map((k) => props.scopes.find((scope) => scope.key === k)?.name ?? k)
    .join(", ");
}

const FIELD_SUBJECTS: Record<string, string> = {
  name: "The name",
  scopes: "The access",
  expires_in_days: "The expiry"
};

function fieldError(field: string): string {
  return (fieldErrors.value[field] ?? [])
    .map((message) =>
      /^[a-z]/.test(message) && FIELD_SUBJECTS[field]
        ? `${FIELD_SUBJECTS[field]} ${message}`
        : message
    )
    .join(" ");
}

function validate(): boolean {
  const errors: Record<string, string[]> = {};
  if (!isRoll.value && !name.value.trim()) {
    errors.name = ["Give the key a name."];
  }
  if (!isRoll.value && !selectedScopes.value.length) {
    errors.scopes = ["Choose at least one."];
  }
  fieldErrors.value = errors;
  return !Object.keys(errors).length;
}

function mint(): Promise<MintedApiKey> {
  const common = { expires_in_days: expiresInDays.value };
  if (props.rolling) {
    return store.dispatch("rollApiKey", {
      apiKeyId: props.rolling.apiKey.id,
      credentialId: props.rolling.credential.id,
      ...common
    });
  }
  return store.dispatch("createApiKey", {
    name: name.value.trim(),
    scopes: selectedScopes.value,
    ...common
  });
}

async function save() {
  baseError.value = "";
  if (!validate()) return;

  saving.value = true;
  try {
    const minted = await mint();
    key.value = minted.key;
    emit("minted", minted.data);
  } catch (error) {
    const parsed = apiKeyError(
      error,
      "The key couldn't be created. Try again."
    );
    fieldErrors.value = parsed.fieldErrors;
    const fieldsShown = Object.keys(parsed.fieldErrors).some(
      (field) => field !== "base" && parsed.fieldErrors[field]?.length
    );
    baseError.value = fieldsShown ? "" : parsed.message;
  } finally {
    saving.value = false;
  }
}

async function copyKey() {
  if (!key.value) return;
  try {
    await navigator.clipboard.writeText(key.value);
    copied.value = true;
    setTimeout(() => (copied.value = false), 2000);
  } catch {
    window.notify.error("Couldn't copy. Select the key and copy it instead.");
  }
}
</script>
